<template>
  <v-container grid-list-md class="candidate edit qualifications">
    <v-progress-linear
      v-if="loading"
      :color="themes.candidate.light.loading"
      :height="5"
      indeterminate
    />

    <div v-for="group in groupNames" :key="`group-${group}`" class="pb-2">
      <v-layout class="py-2" justify-space-between align-center row>
        <div>
          <div class="subheading">
            <v-menu
              v-if="!groupQualifications[group]"
              nudge-bottom="30"
              max-width="300"
              open-on-hover
              class="px-0"
              bottom
              right
            >
              <v-icon slot="activator" class="pb-1" color="warning">mdi-alert-circle</v-icon>
              <v-card dark>
                <v-card-text> No {{ group.toLowerCase() }} added yet </v-card-text>
              </v-card>
            </v-menu>
            {{ group }}
            <v-menu nudge-bottom="30" class="px-0" open-on-hover bottom right max-width="300">
              <v-icon slot="activator" class="pb-1" color="muted">mdi-help-circle</v-icon>
              <v-card dark>
                <v-card-text>
                  {{ hints[group] }}
                </v-card-text>
              </v-card>
            </v-menu>
          </div>
        </div>

        <div v-if="group != 'Intended / future study programme(s)' || allowIntended">
          <v-btn
            v-if="!showForm[group]"
            @click.stop="showForm[group] = true"
            color="primary"
            round
            icon
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </div>
      </v-layout>

      <NewQualification
        :requestGraduationDate="requestGraduationDate"
        :profile="profile"
        @refresh="refresh"
        :label="`Add ${group.toLowerCase()}`"
        :key="`new-qualification-${group}`"
        v-model="showForm[group]"
      />

      <v-data-iterator
        v-if="groupQualifications[group]"
        :items="groupQualifications[group]"
        content-tag="div"
        style="width: 100%;"
        no-data-text=""
        hide-actions
        expand
      >
        <template slot="item" slot-scope="{ item: qualification }">
          <Qualification
            :requiresQualificationTranscript="requiresQualificationTranscript(qualification.id)"
            :requiresProofOfRegistration="requiresProofOfRegistration(qualification.id)"
            :requestGraduationDate="requestGraduationDate"
            :flags="getFlags(qualification)"
            :qualification="qualification"
            :profile="profile"
            @refresh="refresh"
            class="mb-2"
            :key="`qualification-${qualification.id}`"
            :withComponents="withComponents"
          />
        </template>
      </v-data-iterator>
    </div>
  </v-container>
</template>

<script>
// Components
import NewQualification from '@/components/candidate/profile/edit/qualifications/NewQualification'
import Qualification from '@/components/candidate/profile/edit/qualifications/Qualification'

// Helpers
// import _ from 'lodash'
import { byEndsAt, byLevels } from '@/sorting/ProfileQualification'
import { mode } from 'simple-statistics'

export default {
  components: { NewQualification, Qualification },

  created() {
    this.refresh()
  },

  props: {
    profile: { type: Object, required: true },
    actionItems: Array,
    requestGraduationDate: { type: Boolean, default: true },
    withComponents: { type: Boolean, default: true },
  },

  data() {
    return {
      groupNames: [
        'High school / secondary education',
        'Current study programme(s)',
        'Completed study programme(s)',
        'Intended / future study programme(s)',
      ],
      showForm: {
        'High school / secondary education': false,
        'Current study programme(s)': false,
        'Completed study programme(s)': false,
        'Intended / future study programme(s)': false,
      },
      hints: {
        'High school / secondary education': `Please add your High School education \
        (NOTE: subject/course information is required for bursary applications).`,
        'Current study programme(s)': `Are you currently participating in a study programme which\
        has not yet been completed?`,
        'Completed study programme(s)': `Have you successfully completed any study programme(s)\
        after graduating from High School?`,
        'Intended / future study programme(s)': `Are you intending to enrol for (or start) a NEW\
        study programme in the near future? (maximum 2)`,
      },
      loading: true,
    }
  },

  computed: {
    groupQualifications() {
      if (!this.orderedQualifications[0]) return []

      // return _.groupBy(this.orderedQualifications, q => q.startsAt.substring(0, 4))
      return _.groupBy(this.orderedQualifications, this.getQualificationGroupName)
    },

    qualifications() {
      return this.$store.getters['candidate/profileQualifications/all']
    },

    orderedQualifications() {
      if (!this.qualifications || !this.qualifications[0]) return []

      const qualifications = JSON.parse(JSON.stringify(this.qualifications))
      return qualifications
        .sort(byEndsAt({ asc: false }))
        .sort(byLevels({ country: this.levelsCountryMode(), asc: false }))
    },

    allowIntended() {
      if (this.groupQualifications['Intended / future study programme(s)']) {
        return this.groupQualifications['Intended / future study programme(s)'].length < 2
      }
      return true
    },
  },
  methods: {
    getQualificationGroupName(qualification) {
      if (this.isSecondary(qualification)) return 'High school / secondary education'
      if (new Date() < new Date(qualification.startsAt)) return 'Intended / future study programme(s)'
      if (new Date() < new Date(qualification.endsAt)) return 'Current study programme(s)'
      return 'Completed study programme(s)'
    },

    getFlags(qualification) {
      const flags = _.get(this, 'profile.flags')
      if (!flags) return []
      return flags.filter(f => {
        return f.targetId === qualification.id && f.targetType === 'ProfileQualification'
      })
    },

    isSecondary(qualification) {
      return (
        _.get(qualification, 'qualification.qualificationType.qualificationCategory.name') ===
        'Secondary Education'
      )
    },

    levelsCountryMode() {
      const countries = this.qualifications
        .filter(pq => pq.qualification && pq.qualification.levels && pq.qualification.levels[0])
        .reduce(
          (acc, { qualification: { levels } }) => acc.concat(levels.map(l => l.countryCode)),
          []
        )
      return countries[0] ? mode(countries) : null
    },

    refresh() {
      this.loading = true

      this.$store.dispatch('candidate/profileQualifications/fetchAll').then(() => {
        this.loading = false
      })
    },

    requiresProofOfRegistration(id) {
      if (this.actionItems) {
        return this.actionItems.some(
          a => a.type === 'ActionItem::Registration' && a.target.id === id
        )
      }
      return false
    },

    requiresQualificationTranscript(id) {
      if (this.actionItems) {
        return this.actionItems.some(a => a.type === 'ActionItem::Transcript' && a.target.id === id)
      }
      return false
    },
  },
}
</script>
